import React from 'react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {Route, useRouteMatch} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {Icon} from 'components/ui-kit/Icon';
import {
    ContentColumns,
    CopyButton,
    Id,
    StatusWrapper,
    Title,
    TitleText,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {RouterSwitch, RouterTab, RouterTabList} from 'components/ui-kit/RouterTabs';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {colors} from 'theme';
import {AggregateReport, AggregationStorageLocation} from 'types/documents';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {formatAddress} from 'utils/formatAddress';
import {handleNil} from 'utils/handleNil';
import {formatNumber} from 'utils/number';

import {Products} from './Products';
import {ErrorsWrapper, Error} from './styled';

interface OrdersDetailPageProps {
    data: AggregateReport;
}

export function convertData(data: AggregateReport, t: TFunction): AggregateReport {
    return {
        reportId: handleNil(data?.reportId),
        createdAt: handleNil(data?.createdAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy, HH:mm'),
        ),
        reportType: data?.reportType,
        status: handleNil(data?.status, (value: string) =>
            t(`dictionaries.documents.status.${value}`),
        ),
        errors: data.errors,
        reportInfo: {
            // t('pages.documents.details.Aggregation.groupPackageCount')
            groupPackageCount: handleNil(
                data?.reportInfo?.groupPackageCount,
                (value: string) => `${formatNumber(value)} ${t('')}`,
            ),
            // t('pages.documents.details.Aggregation.transportPackageCount')
            transportPackageCount: handleNil(
                data?.reportInfo?.transportPackageCount,
                (value: string) => `${formatNumber(value)} ${t('')}`,
            ),
            // t('pages.documents.details.Aggregation.documentDate')
            createdAt: handleNil(data?.reportInfo?.createdAt, (value: string) =>
                formatDate(value, 'dd.MM.yyyy, HH:mm'),
            ),
            // t('pages.documents.details.Aggregation.storageLocation')
            storageLocation: handleNil(
                data?.reportInfo?.storageLocation,
                (value: AggregationStorageLocation) => formatAddress(value),
            ),
        },
    };
}

export const Aggregate = ({data}: OrdersDetailPageProps) => {
    const {t} = useTranslation();
    const {path, url} = useRouteMatch();
    const copy = useClipboard();

    const convertedData = React.useMemo(() => convertData(data, t), [data, t]);

    const copyCode = () => {
        copy(convertedData.reportId);
        toast.success(t('pages.documents.details.idCopied'));
    };

    return (
        <>
            <TitleWrapper>
                <TitleText>
                    <Title>
                        {t('pages.documents.details.Aggregation.AggregationId')}{' '}
                        <Id>{convertedData.reportId}</Id>
                    </Title>
                    <CopyButton
                        icon={
                            <Icon
                                source={CopyIcon}
                                width={15}
                                height={15}
                                fillColor={colors.brandPrimary}
                            />
                        }
                        onClick={copyCode}
                        hoverIndication={true}
                        label={t('pages.documents.details.copyId')}
                        labelPlace={'bottom'}
                    />
                </TitleText>
                <StatusWrapper color={DOCUMENT_STATUS_COLORS[data.status ?? 'DEFAULT']}>
                    {convertedData.status}
                </StatusWrapper>
            </TitleWrapper>

            <RouterTabList baseUrl={url}>
                <RouterTab url="">{t('pages.documents.detail.commonInfo')}</RouterTab>
                <RouterTab url="products">{t('pages.documents.detail.products')}</RouterTab>
            </RouterTabList>

            <RouterSwitch rootPath={path}>
                <Route path={`${path}`} exact>
                    <ErrorsWrapper>
                        {convertedData.errors?.map((err, index) => (
                            <Error key={index}>{err.message ?? ''}</Error>
                        ))}
                    </ErrorsWrapper>
                    <ContentColumns
                        data={[
                            {
                                documentDate: convertedData.createdAt,
                                groupPackageCount: convertedData.reportInfo.groupPackageCount,
                                transportPackageCount:
                                    convertedData.reportInfo.transportPackageCount,
                                storageLocation: convertedData.reportInfo.storageLocation,
                            },
                            {},
                        ]}
                        tNameSpace="pages.documents.details.Aggregation"
                    />
                </Route>
                <Route path={`${path}/products`} exact>
                    <Products reportId={convertedData.reportId} />
                </Route>
            </RouterSwitch>
        </>
    );
};
